import React, {useEffect, useState} from "react";
import {Alert, Box, Card, CardContent, Chip, CircularProgress, Divider, Grid, Typography,} from "@mui/material";
import axios from "axios";
import {baseUrl} from "../Univ";
const VaccinationTracking = ({patientId}) => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    /* ============================================================
       AUTH HEADERS
       ============================================================ */
    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    });
    /* ============================================================
       LOAD TRACKING
       ============================================================ */
    useEffect(() => {
        if (patientId) {
            loadTracking();
        } else {
            setRecords([]);
        }
    }, [patientId]);
    const loadTracking = async () => {
        setLoading(true);
        setError("");
        try {
            console.log(
                "Loading vaccination tracking for patient:",
                patientId
            );
            const response = await axios.get(
                `${baseUrl}vaccination/tracking/`,
                {
                    params: {
                        patient_id: patientId,
                    },
                    headers: authHeaders(),
                }
            );
            console.log(
                "Vaccination tracking response:",
                response.data
            );
            let data = [];
            if (Array.isArray(response.data)) {
                data = response.data;
            } else if (
                response.data &&
                Array.isArray(response.data.results)
            ) {
                data = response.data.results;
            } else if (
                response.data &&
                Array.isArray(response.data.data)
            ) {
                data = response.data.data;
            } else if (
                response.data &&
                Array.isArray(response.data.records)
            ) {
                data = response.data.records;
            } else if (
                response.data &&
                typeof response.data === "object"
            ) {
                if (
                    response.data.id ||
                    response.data.vaccine ||
                    response.data.patient
                ) {
                    data = [response.data];
                }
            }
            console.log(
                "Vaccination tracking records:",
                data
            );
            setRecords(data);
        } catch (err) {
            console.error(
                "Tracking loading error:",
                err
            );
            console.error(
                "Tracking backend response:",
                err.response?.data
            );
            setRecords([]);
            setError(
                err.response?.data?.detail ||
                err.response?.data?.message ||
                "Unable to load vaccination tracking."
            );
        } finally {
            setLoading(false);
        }
    };
    /* ============================================================
       SAFE VALUE
       ============================================================ */
    const displayValue = (value, fallback = "-") => {
        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {
            return fallback;
        }
        if (typeof value === "object") {
            return (
                value.name ||
                value.vaccine_name ||
                value.patient ||
                value.my_title ||
                value.title ||
                value.id ||
                fallback
            );
        }
        return String(value);
    };
    /* ============================================================
       FOREIGN KEY ID
       ============================================================ */
    const getForeignKeyId = (value) => {
        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {
            return "-";
        }
        if (typeof value === "object") {
            return value.id || "-";
        }
        return value;
    };
    /* ============================================================
       PATIENT
       ============================================================ */
    const getPatientName = (record) => {
        if (
            record.patient &&
            typeof record.patient === "object"
        ) {
            return (
                record.patient.patient ||
                record.patient.name ||
                record.patient.my_title ||
                "-"
            );
        }
        return record.patient_name || "-";
    };
    const getPatientId = (record) => {
        if (
            record.patient &&
            typeof record.patient === "object"
        ) {
            return (
                record.patient.pat_id ||
                record.patient.id ||
                "-"
            );
        }
        return record.patient || "-";
    };
    /* ============================================================
       VACCINE
       ============================================================ */
    const getVaccineName = (record) => {
        if (
            record.vaccine &&
            typeof record.vaccine === "object"
        ) {
            return (
                record.vaccine.vaccine_name ||
                record.vaccine.name ||
                record.vaccine.my_title ||
                "-"
            );
        }
        return record.vaccine_name || "-";
    };
    /* ============================================================
       APPOINTMENT
       ============================================================ */
    const getAppointmentId = (record) => {
        return getForeignKeyId(record.appointment);
    };
    /* ============================================================
       SCHEDULE
       ============================================================ */
    const getScheduleId = (record) => {
        return getForeignKeyId(record.schedule);
    };
    /* ============================================================
       SCHEDULE DOSE
       ============================================================ */
    const getScheduleDoseId = (record) => {
        return getForeignKeyId(record.schedule_dose);
    };
    /* ============================================================
       STATUS COLOR
       ============================================================ */
    const getStatusColor = (status) => {
        switch ((status || "").toLowerCase()) {
            case "completed":
                return "success";
            case "pending":
                return "warning";
            case "overdue":
                return "error";
            case "scheduled":
                return "info";
            case "upcoming":
                return "info";
            default:
                return "default";
        }
    };
    /* ============================================================
       PATIENT NOT SELECTED
       ============================================================ */
    if (!patientId) {
        return (
            <Alert severity="info">
                Select a patient to view vaccination tracking.
            </Alert>
        );
    }
    /* ============================================================
       UI
       ============================================================ */
    return (
        <Card className="vaccination-card">

            <CardContent>

                {/* ==================================================
                    HEADER
                   ================================================== */}

                <Typography
                    variant="h6"
                    gutterBottom
                >
                    Vaccination Tracking
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{mb: 2}}
                >
                    Complete vaccination tracking history including
                    patient, appointment, schedule, dose and vaccine
                    foreign-key information.
                </Typography>

                <Divider sx={{mb: 3}}/>

                {/* ==================================================
                    LOADING
                   ================================================== */}

                {loading && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 4,
                        }}
                    >
                        <CircularProgress/>
                    </Box>
                )}

                {/* ==================================================
                    ERROR
                   ================================================== */}

                {error && (
                    <Alert severity="error">
                        {error}
                    </Alert>
                )}

                {/* ==================================================
                    EMPTY
                   ================================================== */}

                {!loading &&
                    !error &&
                    records.length === 0 && (
                        <Alert severity="info">
                            No vaccination tracking records found.
                        </Alert>
                    )}

                {/* ==================================================
                    RECORDS
                   ================================================== */}

                {!loading &&
                    !error &&
                    records.length > 0 && (
                        <Grid
                            container
                            spacing={2}
                        >

                            {records.map((record, index) => (
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    lg={4}
                                    key={
                                        record.id ||
                                        `tracking-${index}`
                                    }
                                >

                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: "100%",
                                        }}
                                    >

                                        <CardContent>

                                            {/* ==================================
                                                TRACKING HEADER
                                               ================================== */}

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    alignItems:
                                                        "center",
                                                    mb: 2,
                                                }}
                                            >

                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight={700}
                                                >
                                                    Tracking #{record.id}
                                                </Typography>

                                                <Chip
                                                    size="small"
                                                    label={
                                                        record.status ||
                                                        "-"
                                                    }
                                                    color={getStatusColor(
                                                        record.status
                                                    )}
                                                />

                                            </Box>

                                            <Divider
                                                sx={{mb: 2}}
                                            />

                                            {/* ==================================
                                                PATIENT
                                               ================================== */}

                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={700}
                                                sx={{mb: 1}}
                                            >
                                                Patient
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                DB ID:{" "}
                                                {getForeignKeyId(
                                                    record.patient
                                                )}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Patient ID:{" "}
                                                {getPatientId(record)}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Name:{" "}
                                                {getPatientName(record)}
                                            </Typography>

                                            <Divider
                                                sx={{my: 1.5}}
                                            />

                                            {/* ==================================
                                                VACCINE
                                               ================================== */}

                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={700}
                                                sx={{mb: 1}}
                                            >
                                                Vaccine
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Vaccine ID:{" "}
                                                {getForeignKeyId(
                                                    record.vaccine
                                                )}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Vaccine Name:{" "}
                                                {getVaccineName(record)}
                                            </Typography>

                                            <Divider
                                                sx={{my: 1.5}}
                                            />

                                            {/* ==================================
                                                APPOINTMENT
                                               ================================== */}

                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={700}
                                                sx={{mb: 1}}
                                            >
                                                Appointment
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Appointment ID:{" "}
                                                {getAppointmentId(record)}
                                            </Typography>

                                            {record.appointment &&
                                                typeof record.appointment ===
                                                "object" && (
                                                    <>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Appointment Status:{" "}
                                                            {displayValue(
                                                                record
                                                                    .appointment
                                                                    .status
                                                            )}
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Appointment Date:{" "}
                                                            {displayValue(
                                                                record
                                                                    .appointment
                                                                    .appointment_date
                                                            )}
                                                        </Typography>
                                                    </>
                                                )}

                                            <Divider
                                                sx={{my: 1.5}}
                                            />

                                            {/* ==================================
                                                SCHEDULE
                                               ================================== */}

                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={700}
                                                sx={{mb: 1}}
                                            >
                                                Vaccination Schedule
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Schedule ID:{" "}
                                                {getScheduleId(record)}
                                            </Typography>

                                            {record.schedule &&
                                                typeof record.schedule ===
                                                "object" && (
                                                    <>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Schedule Name:{" "}
                                                            {displayValue(
                                                                record
                                                                    .schedule
                                                                    .name ||
                                                                record
                                                                    .schedule
                                                                    .title ||
                                                                record
                                                                    .schedule
                                                                    .schedule_name
                                                            )}
                                                        </Typography>
                                                    </>
                                                )}

                                            <Divider
                                                sx={{my: 1.5}}
                                            />

                                            {/* ==================================
                                                SCHEDULE DOSE
                                               ================================== */}

                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={700}
                                                sx={{mb: 1}}
                                            >
                                                Schedule Dose
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Schedule Dose ID:{" "}
                                                {getScheduleDoseId(record)}
                                            </Typography>

                                            {record.schedule_dose &&
                                                typeof record.schedule_dose ===
                                                "object" && (
                                                    <>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Dose Number:{" "}
                                                            {displayValue(
                                                                record
                                                                    .schedule_dose
                                                                    .dose_number
                                                            )}
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Dose Name:{" "}
                                                            {displayValue(
                                                                record
                                                                    .schedule_dose
                                                                    .dose_name
                                                            )}
                                                        </Typography>
                                                    </>
                                                )}

                                            <Divider
                                                sx={{my: 1.5}}
                                            />

                                            {/* ==================================
                                                DOSE INFORMATION
                                               ================================== */}

                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={700}
                                                sx={{mb: 1}}
                                            >
                                                Dose Information
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Dose Number:{" "}
                                                {record.dose_number || "-"}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Due Date:{" "}
                                                {record.due_date || "-"}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Completed Date:{" "}
                                                {record.completed_date ||
                                                    "-"}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Overdue Days:{" "}
                                                {record.overdue_days ?? 0}
                                            </Typography>

                                            <Divider
                                                sx={{my: 1.5}}
                                            />

                                            {/* ==================================
                                                REMINDER
                                               ================================== */}

                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={700}
                                                sx={{mb: 1}}
                                            >
                                                Reminder
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Reminder Status:{" "}
                                                {record.reminder_status ||
                                                    "-"}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Reminder Date:{" "}
                                                {record.reminder_date ||
                                                    "-"}
                                            </Typography>

                                            <Divider
                                                sx={{my: 1.5}}
                                            />

                                            {/* ==================================
                                                REMARKS
                                               ================================== */}

                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={700}
                                                sx={{mb: 1}}
                                            >
                                                Remarks
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {record.remarks || "-"}
                                            </Typography>

                                            <Divider
                                                sx={{my: 1.5}}
                                            />

                                            {/* ==================================
                                                AUDIT
                                               ================================== */}

                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={700}
                                                sx={{mb: 1}}
                                            >
                                                Audit Information
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Added By:{" "}
                                                {record.addedby || "-"}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Add Date:{" "}
                                                {record.add_date || "-"}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Add Time:{" "}
                                                {record.add_time || "-"}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                IP Address:{" "}
                                                {record.ip_add || "-"}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Location:{" "}
                                                {record.location ?? "-"}
                                            </Typography>

                                        </CardContent>

                                    </Card>

                                </Grid>
                            ))}

                        </Grid>
                    )}

            </CardContent>

        </Card>
    );
};
export default VaccinationTracking;